import * as React from 'react';

export type SortDirection = 'asc' | 'desc';
export type SortValue = string | number | bigint | boolean | null | undefined;

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export type SortAccessors<T, K extends string> = Record<K, (row: T) => SortValue>;

function rank(v: SortValue): 0 | 1 {
  return v === null || v === undefined || v === '' ? 1 : 0;
}

export function compareSortValues(a: SortValue, b: SortValue): number {
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;
  if (ra === 1) return 0;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if ((typeof a === 'number' || typeof a === 'bigint') && (typeof b === 'number' || typeof b === 'bigint')) {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
}

export function sortRows<T, K extends string>(
  rows: readonly T[],
  accessors: SortAccessors<T, K>,
  sort: SortState<K> | null,
): T[] {
  if (!sort) return [...rows];
  const pick = accessors[sort.key];
  const sign = sort.direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: pick(row) }))
    .sort((a, b) => {
      const emptiness = rank(a.value) - rank(b.value);
      if (emptiness !== 0) return emptiness;
      return sign * compareSortValues(a.value, b.value) || a.index - b.index;
    })
    .map((x) => x.row);
}

export interface SortController<K extends string> {
  sort: SortState<K> | null;
  toggle: (key: K) => void;
}

/**
 * Click-to-sort state for a table: first click sorts ascending, the second
 * flips it, and a third clears back to the table's natural order. Empty
 * values always sink to the bottom regardless of direction.
 */
export function useSortedRows<T, K extends string>(
  rows: readonly T[],
  accessors: SortAccessors<T, K>,
  initial: SortState<K> | null = null,
): { rows: T[] } & SortController<K> {
  const [sort, setSort] = React.useState<SortState<K> | null>(initial);
  const toggle = React.useCallback((key: K) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return initial && initial.key !== key ? initial : null;
    });
  }, [initial]);
  const sorted = React.useMemo(() => sortRows(rows, accessors, sort), [rows, accessors, sort]);
  return { rows: sorted, sort, toggle };
}
